import React, { FC, useContext, useState } from 'react';
import { Asset, Footer, ConfirmationModal } from 'Shared/components';
import { currency } from 'Shared/utils';
import {
  Asset as Assets,
  Button,
  InfoFilledIcon,
  Modal,
  Tooltip,
} from '@clearcover/atlas';
import styles from './styles.module.css';
import { useLazyApi } from 'Shared/services/Api';
import { useHistory, useParams } from 'react-router-dom';
import { validateDraftEffectiveDate } from 'Policies/utils/validateDraftEffectiveDate';
import { DatePickerModal } from 'Shared/components/DatePickerModal';
import { PolicyContext } from 'Policies/utils';
import { useUnderwritingErrors } from 'Shared/hooks/useUnderwritingErrors';

type QuoteBarProps = {
  onSaveDraft: () => void;
  onUpdatePolicy: () => void;
  showQuoteBar: boolean;
  billingChangeSummary: {
    newFullTermValue: number;
    billingChangeValue: number;
    billingChangeMessage: string;
  };
  renewalDraftId?: string;
};

export const QuoteBar: FC<QuoteBarProps> = ({
  onSaveDraft,
  onUpdatePolicy,
  showQuoteBar,
  billingChangeSummary: {
    billingChangeMessage,
    billingChangeValue,
    newFullTermValue,
  },
  renewalDraftId,
}) => {
  const history = useHistory();
  const { id: policyId, draftId } = useParams<{
    id: string;
    draftId: string;
  }>();
  const {
    flags: { isFailingUnderwriting },
    details: { draftType, isRenewalDraft },
  } = useContext(PolicyContext);
  const { underwritingErrors, setUnderwritingErrors } = useUnderwritingErrors();

  const [{ loading }, executeDateUpdate] = useLazyApi<{
    newDraftId: string;
  }>({
    url: `/policies/drafts/dateUpdate`,
    method: 'POST',
  });

  const [
    { loading: terminateDraftloading, error: terminateDraftError },
    executeTerminateDraft,
  ] = useLazyApi({
    url: `policies/${policyId}/drafts/${draftId}/terminate`,
    method: 'POST',
  });

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );

  const premiumIncrease = billingChangeValue > 0;

  if (!showQuoteBar) return <></>;
  return (
    <Footer hasSecondaryMenu={false}>
      {loading ? (
        <div className={styles.loadingContainer}>
          <Asset variant="loading" />
        </div>
      ) : (
        <div className={styles.quoteBar} data-testid={'quote-bar'}>
          <DatePickerModal
            classNames={styles.datePicker}
            label={'Effective Date of Change'}
            disabled={draftType === 'renewal_quote'}
            onDateChange={(effectiveDate: string) => {
              setUnderwritingErrors(null);
              setErrorMessage(undefined);
              if (validateDraftEffectiveDate(effectiveDate, setErrorMessage)) {
                executeDateUpdate({
                  data: {
                    effectiveDate,
                    policyId,
                    draftId,
                  },
                })
                  .then(({ data: { newDraftId } }) => {
                    history.push(`/policies/${policyId}/draft/${newDraftId}`);
                  })
                  .catch(err => {
                    setUnderwritingErrors(err);
                    setErrorMessage(err.message);
                  });
              }
            }}
            errorMessage={underwritingErrors[0] || errorMessage}
          />

          <div className={styles.rate}>
            <div className={styles.rateHeader}>New Full Term Premium</div>
            {isFailingUnderwriting ? (
              <div className={styles.underwritingErrMsg}>
                <Assets
                  icon={InfoFilledIcon}
                  height={20}
                  width={20}
                  description="Error Icon"
                />
                <span>Outstanding Rule Failures</span>
              </div>
            ) : (
              <>
                <div className={styles.additionalCost}>
                  <span>{currency(Math.abs(newFullTermValue)).format()}</span>
                  <span
                    className={
                      premiumIncrease ? styles.positive : styles.negative
                    }
                  >
                    {`${premiumIncrease ? '+' : '-'}${currency(
                      Math.abs(billingChangeValue)
                    ).format()}`}
                  </span>
                </div>
                {/* TODO: Update styling when this isn't here */}
                {draftType !== 'renewal_quote' && (
                  <p className={styles.rateFooter}>{billingChangeMessage}</p>
                )}
              </>
            )}
          </div>
          <div className={styles.buttonGroup}>
            {!isRenewalDraft && (
              <Modal
                variant="normal"
                toggle={openModal => (
                  <Button
                    data-testid="quote-bar-exit"
                    variant="danger"
                    onClick={openModal}
                  >
                    Delete Draft
                  </Button>
                )}
              >
                {close => (
                  <ConfirmationModal
                    onSubmit={() => {
                      executeTerminateDraft().then(() => {
                        if (draftType === 'renewal_quote') {
                          // Note: Although `renewalDraftId` is nullable, it will always have a value
                          // for renewal quotes, as they are built off of an existing renewal drafts.
                          // If the renewal draft is terminated or applied, the renewal quote will be
                          // discarded automatically.
                          history.push(
                            `/policies/${policyId}/draft/${renewalDraftId}`
                          );
                        } else {
                          history.push(`/policies/${policyId}`);
                        }
                      });
                    }}
                    close={close}
                    isSubmitting={terminateDraftloading}
                    error={terminateDraftError?.message}
                    headerText="Confirm discarding draft"
                    bodyText="Discarding draft will result in losing your current draft changes. Please confirm."
                  />
                )}
              </Modal>
            )}

            <Button
              data-testid="quote-bar-save"
              size="md"
              variant="secondary"
              disabled={!!errorMessage}
              onClick={() => onSaveDraft()}
            >
              Exit Draft
            </Button>
            {!isRenewalDraft && (
              <Tooltip
                position="top"
                tooltip={
                  isFailingUnderwriting ? 'Resolve outstanding errors' : ''
                }
              >
                <Button
                  data-testid="quote-bar-update"
                  size="md"
                  variant="primary"
                  disabled={!!errorMessage || isFailingUnderwriting}
                  onClick={() => onUpdatePolicy()}
                >
                  Review Changes
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
      )}
    </Footer>
  );
};
